// ════════════════════════════════════════════════════════════════════
// IMPORT PLANNER  (Duplicate Prevention Dashboard backend)
//
// Two-phase import flow:
//
//   1. PLAN — planImport() takes parsed rows + the operator's
//      existing roster, runs the 5-rule matcher across every
//      row, and produces a "plan" object the UI renders as the
//      Duplicate Prevention Dashboard:
//
//        { newClients: [...]      — no existing match found
//          updates:    [...]      — match found; client + policy update
//          duplicates: [...]      — multiple existing match candidates
//                                   OR same-batch row collision
//          failed:     [...]      — missing required data
//          stats:      { ... }    — top-level counts }
//
//   2. COMMIT — commitImport() takes the (possibly user-edited)
//      plan and runs the inserts/updates. Writes an import_runs
//      row + one import_run_items row per row for the History
//      page. Fires audit_logs entries for every client create
//      and update so the agent audit log shows who did what.
//
// USAGE
// ──────────────────────────────────────────────────────────────────
//   import { planImport, commitImport } from './lib/importPlanner';
//
//   const ctx  = await loadOwnerMatcherContext(ownerId);
//   const plan = planImport({ ownerId, parsedRows, format, ctx });
//
//   // render dashboard, agent reviews, optionally toggles dupes...
//
//   const result = await commitImport({ ownerId, currentUser, plan,
//                                       fileName, format });
//
// The flow is intentionally pure on the planning side — no DB
// writes happen until commitImport. That makes the preview safe
// to discard, re-plan, or cancel without side-effects.
// ════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';
import { insertClientSafe, updateClientSafe } from './clientWrite';
import {
  buildCandidate, matchClientInMemory, diffClient, normalizePhone, fuzzyNameMatch,
} from './clientMatcher';
import { audit, AUDIT_ACTION } from './auditLog';

// ── Plan ────────────────────────────────────────────────────────
// `parsedRows` is the array a format-specific parser produced.
// Each row should at minimum have the fields buildCandidate()
// recognizes — most parsers already match (HCSC, Jarvis,
// HealthSherpa, generic CSV). The optional `extractor` lets a
// caller override the row → candidate mapping when needed.
export function planImport({ parsedRows = [], ownerId, format, ctx, extractor }) {
  const stats = {
    totalRows: parsedRows.length,
    newClients: 0,
    updates: 0,
    duplicates: 0,
    failed: 0,
  };
  const newClients = [];   // rows that will create
  const updates    = [];   // rows that will update
  const duplicates = [];   // need agent resolution
  const failed     = [];   // missing required data

  // Track candidates we've already planned in this batch so two
  // rows pointing at the SAME person produce one client + multiple
  // policies, not two new clients.
  const inBatchClients = []; // [{ key, candidate, plannedAs }]
  const keyOf = (cand) => [
    cand.email,
    cand.phone,
    cand.dob && cand.lastName ? `${cand.lastName.toLowerCase()}|${cand.dob}` : null,
    cand.address && cand.lastName ? `${cand.lastName.toLowerCase()}|${cand.address.toLowerCase()}` : null,
  ].filter(Boolean);

  parsedRows.forEach((row, idx) => {
    let candidate;
    try {
      candidate = extractor ? extractor(row, format) : extractDefaultCandidate(row, format);
    } catch (err) {
      failed.push({ rowIndex: idx + 1, row, reason: `Could not read row: ${err.message || err}` });
      stats.failed++;
      return;
    }
    candidate.format = format || candidate.format || null;

    // Validation: need at least a name. Without a name we can't
    // do anything useful — even rule 5 needs a name.
    if (!candidate.name) {
      failed.push({ rowIndex: idx + 1, row, candidate, reason: 'Missing client name.' });
      stats.failed++;
      return;
    }

    // 1. Try to match against an existing client in the DB.
    const dbMatch = matchClientInMemory({
      ownerId, candidate,
      clients: ctx.clients,
      policyIndex: ctx.policyIndex,
    });

    if (dbMatch.match) {
      // Existing client found → update + attach policy.
      const changes = diffClient(dbMatch.match, candidate);
      updates.push({
        rowIndex: idx + 1, row, candidate,
        existing: dbMatch.match,
        matchedOn: dbMatch.matchedOn,
        changes,
      });
      stats.updates++;
      return;
    }

    // 2. Same-batch dedup — does another row in THIS file already
    //    point at this person? If so, attach the policy to that
    //    planned client instead of creating another.
    const myKeys = keyOf(candidate);
    const sameBatch = inBatchClients.find(b => b.key.some(k => myKeys.includes(k)));
    if (sameBatch) {
      // Treat the second occurrence as a duplicate — agent reviews.
      duplicates.push({
        rowIndex: idx + 1, row, candidate,
        kind: 'intra_batch',
        sameAsRowIndex: sameBatch.rowIndex,
      });
      stats.duplicates++;
      return;
    }

    // 3. Otherwise this is a brand-new client.
    inBatchClients.push({ key: myKeys, candidate, rowIndex: idx + 1 });
    newClients.push({ rowIndex: idx + 1, row, candidate });
    stats.newClients++;
  });

  return { stats, newClients, updates, duplicates, failed };
}

// Default extractor handles the row shapes produced by the four
// parsers we ship today. Each parser uses different field names
// (HealthSherpa has 188 columns, HCSC's are abbreviated, Jarvis
// uses camelCase). Callers can pass their own `extractor` when
// the row shape doesn't match these.
function extractDefaultCandidate(row, format) {
  if (format === 'hcsc') {
    return buildCandidate({
      fullName:     row.insured_name,
      phone:        null,
      dob:          null,
      policyNumber: row.policy_number,
      carrier:      'BCBS (HCSC)',
      planType:     'aca_marketplace',
      format,
    });
  }
  if (format === 'jarvis') {
    return buildCandidate({
      fullName:     row.client_name,
      phone:        row.client_phone,
      email:        row.client_email,
      dob:          row.client_dob,
      address:      row._address ? [
                      row._address.address1, row._address.address2,
                      `${row._address.city || ''}, ${row._address.state || ''} ${row._address.zip || ''}`.replace(/\s+,/, ',').trim(),
                    ].filter(Boolean).join(' ') : null,
      carrier:      row.carrier,
      planType:     row.plan_type,
      format,
    });
  }
  // healthsherpa / generic_csv: same shape as the rest of the
  // health_policies upsert pipeline.
  return buildCandidate({
    fullName:     row.client_name,
    phone:        row.client_phone,
    email:        row.client_email,
    dob:          row.client_dob,
    address:      row.client_address || row.address,
    policyNumber: row.member_id,
    carrier:      row.carrier,
    planType:     row.plan_type,
    format,
  });
}

// ── Commit ──────────────────────────────────────────────────────
// Runs every action in the plan against the database, logs the
// import_runs + import_run_items rows, and fires audit_logs for
// every client create / update.
//
// `decisions` is an optional map { rowIndex: 'create_new'|'merge'|'skip' }
// the agent toggles on flagged duplicates. Defaults to 'skip' for
// untouched dupes.
export async function commitImport({
  ownerId, currentUser, plan,
  fileName = null, fileSizeBytes = null, format = null, sourceCarrier = null,
  decisions = {},
  // The caller supplies the "create the actual policy row" function
  // since policy creation varies by source (HCSC has chargeback
  // payloads, etc.). The default just inserts into health_policies.
  createPolicy = defaultCreateHealthPolicy,
  updatePolicy = defaultUpdateHealthPolicy,
}) {
  const counts = {
    newClients: 0, updatedClients: 0, newPolicies: 0,
    updatedPolicies: 0, skipped: 0, failed: 0, duplicateWarnings: plan.duplicates.length,
  };

  // 1. Open the import_runs row.
  //
  // Resilience: if migration 040 hasn't landed yet (or PostgREST's
  // schema cache hasn't refreshed since it did), import_runs is
  // missing. We DON'T want that to block the actual policy commit —
  // the agent needs the BoB loaded TODAY, the audit trail can come
  // later. So: try to open the run; if it fails because the table is
  // missing, set runId=null and the per-row import_run_items inserts
  // below also skip. The clients + health_policies writes proceed
  // normally.
  let runId = null;
  let auditLogEnabled = true;
  const runIns = await supabase.from('import_runs').insert({
    owner_id:        ownerId,
    actor_id:        currentUser?.id || null,
    actor_name:      currentUser?.name || null,
    file_name:       fileName,
    file_size_bytes: fileSizeBytes,
    format,
    source_carrier:  sourceCarrier,
    total_rows:      plan.stats.totalRows,
    status:          'planned',
  }).select().single();
  if (runIns.error) {
    // Postgres-side missing-table error vs PostgREST schema-cache
    // error look different but both should fall through to graceful
    // degradation. Anything else (RLS, etc.) is a real error — throw.
    const msg = String(runIns.error.message || '').toLowerCase();
    const isSchemaCache = msg.includes('schema cache') || msg.includes('does not exist') || msg.includes('relation');
    if (!isSchemaCache) throw runIns.error;
    // eslint-disable-next-line no-console
    console.warn('[importPlanner] import_runs unavailable — committing without audit log.', runIns.error.message);
    auditLogEnabled = false;
  } else {
    runId = runIns.data.id;
  }

  const itemRows = []; // import_run_items inserts batched at end (only when auditLogEnabled)

  // 2. New clients — insert client + first policy.
  for (const item of plan.newClients) {
    try {
      const clientRow = await insertClient(ownerId, item.candidate);
      const policyRow = await createPolicy({
        ownerId, clientId: clientRow.id, candidate: item.candidate, row: item.row,
      });
      itemRows.push({
        import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
        outcome: 'new_client', client_id: clientRow.id, policy_id: policyRow?.id || null,
        matched_on: null, changes: {}, candidate: pruneCandidate(item.candidate),
      });
      counts.newClients++;
      if (policyRow) counts.newPolicies++;

      audit({
        ownerId, actorId: currentUser?.id, actorName: currentUser?.name,
        targetType: 'clients', targetId: clientRow.id, targetLabel: clientRow.name,
        action: AUDIT_ACTION.LEAD_CREATED,
        metadata: { source: 'import', format, fileName, rowIndex: item.rowIndex },
      });
    } catch (err) {
      counts.failed++;
      itemRows.push({
        import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
        outcome: 'failed', candidate: pruneCandidate(item.candidate),
        reason: err.message || String(err),
      });
    }
  }

  // 3. Updates — patch the client + attach the policy.
  for (const item of plan.updates) {
    try {
      // Apply diff fields back to the client row.
      const diffPatch = {};
      for (const [field, [, newV]] of Object.entries(item.changes || {})) {
        diffPatch[field] = newV;
      }
      let outcome = 'updated_client';
      if (Object.keys(diffPatch).length > 0) {
        const { error } = await updateClientSafe(diffPatch, item.existing.id);
        if (error) throw new Error(error.message);
      }
      // Attach the new policy.
      const policyRow = await createPolicy({
        ownerId, clientId: item.existing.id, candidate: item.candidate, row: item.row,
      });
      if (policyRow) {
        counts.newPolicies++;
        outcome = 'new_policy';
      }
      counts.updatedClients++;

      itemRows.push({
        import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
        outcome, client_id: item.existing.id, policy_id: policyRow?.id || null,
        matched_on: item.matchedOn, changes: item.changes || {},
        candidate: pruneCandidate(item.candidate),
      });

      audit({
        ownerId, actorId: currentUser?.id, actorName: currentUser?.name,
        targetType: 'clients', targetId: item.existing.id, targetLabel: item.existing.name,
        action: AUDIT_ACTION.NOTE_ADDED,
        metadata: {
          source: 'import',
          format,
          matchedOn: item.matchedOn,
          changes: item.changes,
          fileName,
          rowIndex: item.rowIndex,
        },
      });
    } catch (err) {
      counts.failed++;
      itemRows.push({
        import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
        outcome: 'failed', candidate: pruneCandidate(item.candidate),
        reason: err.message || String(err),
      });
    }
  }

  // 4. Duplicate-flagged rows — honor agent's decision.
  for (const item of plan.duplicates) {
    const decision = decisions[item.rowIndex] || 'skip';
    if (decision === 'skip') {
      counts.skipped++;
      itemRows.push({
        import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
        outcome: 'skipped', candidate: pruneCandidate(item.candidate),
        reason: 'Duplicate flagged; agent chose to skip.',
      });
      continue;
    }
    // 'create_new' or 'merge' — treat 'create_new' as a forced new client.
    try {
      if (decision === 'merge' && item.matchClientId) {
        const policyRow = await createPolicy({
          ownerId, clientId: item.matchClientId, candidate: item.candidate, row: item.row,
        });
        if (policyRow) counts.newPolicies++;
        counts.updatedClients++;
        itemRows.push({
          import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
          outcome: 'updated_client', client_id: item.matchClientId,
          policy_id: policyRow?.id || null, matched_on: 'fuzzy_name',
          candidate: pruneCandidate(item.candidate),
        });
      } else {
        const clientRow = await insertClient(ownerId, item.candidate);
        const policyRow = await createPolicy({
          ownerId, clientId: clientRow.id, candidate: item.candidate, row: item.row,
        });
        counts.newClients++;
        if (policyRow) counts.newPolicies++;
        itemRows.push({
          import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
          outcome: 'new_client', client_id: clientRow.id, policy_id: policyRow?.id || null,
          candidate: pruneCandidate(item.candidate),
        });
      }
    } catch (err) {
      counts.failed++;
      itemRows.push({
        import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
        outcome: 'failed', candidate: pruneCandidate(item.candidate),
        reason: err.message || String(err),
      });
    }
  }

  // 5. Failed-at-plan rows — log them as failed in the run too.
  for (const item of plan.failed) {
    itemRows.push({
      import_run_id: runId, owner_id: ownerId, row_index: item.rowIndex,
      outcome: 'failed', candidate: pruneCandidate(item.candidate || {}),
      reason: item.reason,
    });
    counts.failed++;
  }

  // 6. Insert all items in one batch — skip silently if the audit
  // log isn't available. The actual client + policy writes have
  // already landed by this point; the audit row is bonus context.
  if (auditLogEnabled && itemRows.length > 0) {
    const itemsIns = await supabase.from('import_run_items').insert(itemRows);
    if (itemsIns.error) {
      // eslint-disable-next-line no-console
      console.warn('[importPlanner] import_run_items insert failed (continuing):', itemsIns.error.message);
    }
  }

  // 7. Stamp counts + close the run.
  if (auditLogEnabled && runId) {
    await supabase.from('import_runs').update({
      new_clients:        counts.newClients,
      updated_clients:    counts.updatedClients,
      new_policies:       counts.newPolicies,
      updated_policies:   counts.updatedPolicies,
      skipped:            counts.skipped,
      failed:             counts.failed,
      duplicate_warnings: counts.duplicateWarnings,
      status:             counts.failed > 0 && counts.newClients + counts.updatedClients === 0 ? 'cancelled'
                        : counts.failed > 0 ? 'partial' : 'committed',
      committed_at:       new Date().toISOString(),
    }).eq('id', runId);
  }

  return { runId, counts };
}

// ── Helpers ─────────────────────────────────────────────────────
async function insertClient(ownerId, candidate) {
  const row = {
    owner_id:       ownerId,
    name:           candidate.name,
    phone:          candidate.phone || null,
    email:          candidate.email || null,
    dob:            candidate.dob   || null,
    address:        candidate.address || null,
    carrier:        candidate.carrier || null,
    policy:         candidate.policyNumber || null,
    vertical:       candidate.planType && (
                      candidate.planType.startsWith('medicare')   ? 'health'
                    : candidate.planType.startsWith('aca')        ? 'health'
                    : candidate.planType.startsWith('dental')     ? 'health'
                    : candidate.planType.startsWith('hospital')   ? 'health'
                    : candidate.planType.startsWith('short_term') ? 'health'
                    : candidate.planType.startsWith('private_ppo')? 'health'
                    : 'life'),
  };
  const { data, error } = await insertClientSafe(row, '*');
  if (error) throw new Error(error.message);
  return data;
}

async function defaultCreateHealthPolicy({ ownerId, clientId, candidate }) {
  if (!candidate.planType && !candidate.carrier) return null;
  const row = {
    owner_id:    ownerId,
    client_id:   clientId,
    client_name: candidate.name,
    client_phone:candidate.phone || null,
    client_email:candidate.email || null,
    client_dob:  candidate.dob   || null,
    carrier:     candidate.carrier || null,
    plan_type:   candidate.planType || 'other',
    member_id:   candidate.policyNumber || null,
    status:      'active',
    lead_source: candidate.format ? `Import · ${candidate.format}` : 'Import',
  };
  const { data, error } = await supabase.from('health_policies').insert(row).select().single();
  if (error) throw error;
  return data;
}

async function defaultUpdateHealthPolicy() { return null; /* not used yet */ }

// Trim the candidate object before persisting to import_run_items.
// We want the audit trail of "what came in from the file" but not
// the entire raw row (the parser put it through buildCandidate
// already — this is what survives).
function pruneCandidate(c = {}) {
  return {
    name:         c.name || null,
    firstName:    c.firstName || null,
    lastName:     c.lastName || null,
    email:        c.email || null,
    phone:        c.phone || null,
    dob:          c.dob || null,
    address:      c.address || null,
    policyNumber: c.policyNumber || null,
    carrier:      c.carrier || null,
    planType:     c.planType || null,
  };
}
